// Доменные секции мира: инфраструктура, угрозы, преемственность
// (карточки + история). Общий модуль для WorldTab и ChronicleTab.
// Вынесено из CampaignSetupPage.tsx без изменения поведения (Фаза 1).

import {
  type CampaignContinuity,
  type CampaignContinuityItem,
  type CampaignThreatOverview,
  type CampaignThreat,
  type CampaignInfrastructureOverview,
} from "../api";
import { BlockHeader, ParagraphText, ProgressBar } from "./shared";
import {
  WorldAssetIcon,
  WorldSectionAssetHeader,
  continuityAsset,
  infrastructureCategoryAsset,
  infrastructureProjectAsset,
} from "./worldAssets";

function InfrastructureWorldSection({
  infrastructure,
}: {
  infrastructure: CampaignInfrastructureOverview;
}) {
  const summary = infrastructure.summary;
  return (
    <div className="cc-tab-stack cc-infrastructure-world">
      <section className="cc-block cc-infrastructure-summary">
        <WorldSectionAssetHeader
          eyebrow="Долгая экономика города"
          title={summary.label}
          kind="system"
          code="system"
        />
        <ParagraphText text={summary.summary} />
        <div className="cc-infrastructure-kpis">
          <span>
            <b>{summary.capacityScore}</b>
            <small>запас прочности</small>
          </span>
          <span>
            <b>{summary.maintenanceLoad}/10</b>
            <small>средняя нагрузка</small>
          </span>
          <span>
            <b>{summary.activeProjectCount}</b>
            <small>проектов в работе</small>
          </span>
          <span>
            <b>{summary.bottleneckCount}</b>
            <small>узких мест</small>
          </span>
        </div>
        <p className="cc-help-line">
          Инфраструктура не заменяет ресурсы: она показывает, во что
          превращаются успешные долгосрочные инициативы и какие системы требуют
          обслуживания. Узкое место — это направление, где нагрузка уже выше
          возможностей: связанные действия и инициативы становятся рискованнее,
          пока Совет не снизит нагрузку или не усилит ёмкость проектами.
        </p>
      </section>

      <section className="cc-block">
        <WorldSectionAssetHeader
          eyebrow="Ёмкость города"
          title="Инфраструктурные направления"
          kind="institution"
          code="governance"
        />
        {infrastructure.capacities.length === 0 ? (
          <p className="cc-empty-inline">
            Инфраструктурные направления появятся после создания мира.
          </p>
        ) : (
          <div className="cc-infrastructure-grid">
            {infrastructure.capacities.map((capacity) => (
              <article
                className={`cc-infrastructure-capacity ${capacity.status.toLowerCase()}`}
                key={capacity.code}
              >
                <header>
                  <div className="cc-world-card-head">
                    <WorldAssetIcon
                      kind={infrastructureCategoryAsset(capacity.category).kind}
                      code={infrastructureCategoryAsset(capacity.category).code}
                      label={capacity.displayName}
                    />
                    <div>
                      <small>{capacity.categoryLabel}</small>
                      <h3>{capacity.displayName}</h3>
                    </div>
                  </div>
                  <b>{capacity.statusLabel}</b>
                </header>
                <div className="cc-infrastructure-levels">
                  <span>
                    Уровень <b>{capacity.level}</b>
                  </span>
                  <span>
                    Нагрузка <b>{capacity.load}/10</b>
                  </span>
                  {capacity.relatedProjectCount > 0 && (
                    <span>
                      Связанных проектов <b>{capacity.relatedProjectCount}</b>
                    </span>
                  )}
                </div>
                <ParagraphText text={capacity.capacityHint} />
                <div className="cc-infrastructure-maintenance">
                  <strong>Обслуживание</strong>
                  <ParagraphText text={capacity.maintenanceHint} />
                </div>
                {capacity.relatedResources.length > 0 && (
                  <div className="cc-infrastructure-resources">
                    {capacity.relatedResources.map((resource) => (
                      <span key={resource.code}>
                        <b>{resource.displayName}</b> {resource.currentValue}/
                        {resource.maxValue} · {resource.pressureLabel}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="cc-block">
        <WorldSectionAssetHeader
          eyebrow="Портфель строительства"
          title="Долгосрочные проекты"
          kind="system"
          code="system"
        />
        <ParagraphText text={infrastructure.projects.summary} />
        {infrastructure.projects.active.length === 0 &&
        infrastructure.projects.completed.length === 0 &&
        infrastructure.projects.stalled.length === 0 ? (
          <p className="cc-empty-inline">
            Проектов пока нет. Они появятся из успешных инициатив строительства,
            восстановления, программ, сетей и систем.
          </p>
        ) : (
          <div className="cc-infrastructure-project-groups">
            {infrastructure.projects.active.length > 0 && (
              <InfrastructureProjectGroup
                title="В работе"
                items={infrastructure.projects.active}
              />
            )}
            {infrastructure.projects.completed.length > 0 && (
              <InfrastructureProjectGroup
                title="Завершены"
                items={infrastructure.projects.completed}
              />
            )}
            {infrastructure.projects.stalled.length > 0 && (
              <InfrastructureProjectGroup
                title="Остановлены"
                items={infrastructure.projects.stalled}
              />
            )}
          </div>
        )}
      </section>
    </div>
  );
}
function InfrastructureProjectGroup({
  title,
  items,
}: {
  title: string;
  items: CampaignInfrastructureOverview["projects"]["active"];
}) {
  return (
    <section className="cc-infrastructure-project-group">
      <div className="cc-world-inline-section-heading">
        <WorldAssetIcon kind="system" code="system" label={title} />
        <h3>{title}</h3>
      </div>
      <div className="cc-infrastructure-project-list">
        {items.map((item) => (
          <article className="cc-infrastructure-project" key={item.id}>
            <header>
              <div className="cc-world-card-head">
                <WorldAssetIcon
                  kind={infrastructureProjectAsset(item).kind}
                  code={infrastructureProjectAsset(item).code}
                  label={item.title}
                />
                <div>
                  <span>{item.kindLabel}</span>
                  <h4>{item.title}</h4>
                </div>
              </div>
              <b>{item.statusLabel}</b>
            </header>
            <div className="cc-continuity-progress">
              <ProgressBar value={item.progressPercent} />
              <span>{item.progressPercent}%</span>
            </div>
            <p>
              <strong>{item.healthLabel}</strong>
            </p>
            <div className="cc-infrastructure-project-note">
              <small>Ёмкость</small>
              <ParagraphText text={item.capacityEffect} />
            </div>
            <div className="cc-infrastructure-project-note">
              <small>Обслуживание</small>
              <ParagraphText text={item.maintenanceProfile} />
            </div>
            {item.nextMilestoneText && (
              <div className="cc-infrastructure-project-note">
                <small>Следующий рубеж</small>
                <ParagraphText text={item.nextMilestoneText} />
              </div>
            )}
            {item.dueCycleNumber && (
              <footer>Срок: цикл {item.dueCycleNumber}</footer>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
function ThreatWorldSection({ threats }: { threats: CampaignThreatOverview }) {
  return (
    <div className="cc-tab-stack cc-threat-world">
      {threats.defeat && (
        <section className="cc-block cc-defeat-banner">
          <WorldSectionAssetHeader
            eyebrow="Коллективное поражение"
            title={threats.defeat.title}
            kind="threat"
            code={threats.defeat.title}
          />
          <ParagraphText text={threats.defeat.summary} />
          <p>
            Обычные игровые циклы больше не открываются. История кампании
            сохраняется в Хронике.
          </p>
        </section>
      )}
      <section className="cc-block">
        <WorldSectionAssetHeader
          eyebrow="Давление на город"
          title="Угрозы долгосрочной кампании"
          kind="threat"
          code="collapse"
        />
        <p className="cc-form-intro">
          Угрозы создаются из реальных слабых мест этого мира и меняются только
          по рассчитанным последствиям решений. Чем опаснее положение, тем
          сильнее оно влияет на ситуации следующих циклов.
        </p>
        <div className="cc-threat-rules">
          <span>
            <b>Критическая граница</b> — город получает последнее
            предупреждение.
          </span>
          <span>
            <b>Коллективное поражение</b> — одна угроза становится необратимой
            либо несколько угроз остаются у предела два цикла подряд.
          </span>
        </div>
      </section>
      {threats.tracks.length === 0 ? (
        <section className="cc-block empty-state">
          <h3>Угрозы ещё не сформированы</h3>
          <p>
            Они появятся после создания мира и будут связаны с его ресурсами и
            показателями.
          </p>
        </section>
      ) : (
        <section className="cc-block">
          <div className="cc-threat-grid">
            {threats.tracks.map((threat) => (
              <ThreatCard threat={threat} key={threat.id} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
function ThreatCard({ threat }: { threat: CampaignThreat }) {
  return (
    <article className={`cc-threat-card ${threat.level.toLowerCase()}`}>
      <header>
        <div className="cc-world-card-head cc-threat-card-copy">
          <WorldAssetIcon kind="threat" code={threat.title} label={threat.title} />
          <div>
            <small>{threatLevelLabel(threat.level)}</small>
            <h3>{threat.title}</h3>
          </div>
        </div>
        <strong>
          {threat.currentValue}
          <small>/100</small>
        </strong>
      </header>
      <ProgressBar value={threat.currentValue} />
      <ParagraphText text={threat.description} />
      <div className="cc-threat-warning">
        <b>Сейчас</b>
        <ParagraphText text={threat.warningText} />
      </div>
      <footer>
        <span>Связано: {threat.linkedDisplayName}</span>
        {threat.criticalCycles > 0 && (
          <span>У предела: {threat.criticalCycles} цикл.</span>
        )}
      </footer>
    </article>
  );
}
function ThreatHistory({ threats }: { threats: CampaignThreatOverview }) {
  const byId = new Map(threats.tracks.map((item) => [item.id, item]));
  return (
    <div className="cc-tab-stack cc-threat-history">
      {threats.defeat && (
        <section className="cc-block cc-defeat-banner">
          <BlockHeader
            eyebrow="Финал кампании"
            title={threats.defeat.title}
            icon="book"
          />
          <ParagraphText
            className="narrative-copy"
            text={threats.defeat.summary}
          />
          <time>
            {new Date(threats.defeat.createdAt).toLocaleString("ru-RU")}
          </time>
        </section>
      )}
      <section className="cc-block">
        <BlockHeader
          eyebrow="История давления"
          title="Как менялись угрозы"
          icon="activity"
        />
        <p className="cc-form-intro">
          Здесь сохраняются только значимые изменения: усиление, ослабление,
          переход на новую ступень опасности и коллективное поражение.
        </p>
        {threats.recentEvents.length === 0 ? (
          <p className="cc-empty-inline">
            Изменений угроз пока не зафиксировано.
          </p>
        ) : (
          <div className="cc-threat-timeline">
            {threats.recentEvents.map((event) => {
              const threat = byId.get(event.threatId);
              return (
                <article
                  key={event.id}
                  className={event.eventType.toLowerCase()}
                >
                  <span className="cc-threat-timeline-dot" />
                  <div>
                    <small>
                      {new Date(event.createdAt).toLocaleString("ru-RU")} ·{" "}
                      {threatEventLabel(event.eventType)}
                    </small>
                    <h3>{event.title}</h3>
                    {threat && <b>{threat.title}</b>}
                    <ParagraphText text={event.narrativeText} />
                    <span>
                      {threatLevelLabel(event.beforeLevel)} →{" "}
                      {threatLevelLabel(event.afterLevel)}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
function threatLevelLabel(level: CampaignThreat["level"]) {
  return {
    STABLE: "Под контролем",
    TENSE: "Напряжение",
    DANGEROUS: "Опасно",
    CRITICAL: "Критически",
    COLLAPSED: "Необратимо",
  }[level];
}
function threatEventLabel(
  type: CampaignThreatOverview["recentEvents"][number]["eventType"],
) {
  return {
    CREATED: "Возникновение",
    ESCALATED: "Усиление",
    REDUCED: "Ослабление",
    WARNING: "Предупреждение",
    COLLAPSED: "Коллапс",
    OBSERVED: "Без изменений",
  }[type];
}
function ContinuityWorldSection({
  continuity,
}: {
  continuity: CampaignContinuity;
}) {
  const groups = continuityTypeOrder
    .map((type) => ({
      type,
      items: continuity.active.filter((item) => item.type === type),
    }))
    .filter((group) => group.items.length > 0);
  return (
    <div className="cc-tab-stack cc-continuity-world">
      <section className="cc-block">
        <WorldSectionAssetHeader
          eyebrow="Наследие решений"
          title="Проекты и договорённости"
          kind="system"
          code="system"
        />
        <p className="cc-form-intro">
          Здесь собраны последствия, которые продолжают действовать после
          окончания игрового дня. Они переходят в контекст следующих циклов и
          могут получить развитие новыми решениями.
        </p>
      </section>
      {groups.length === 0 ? (
        <section className="cc-block empty-state">
          <h3>Долгосрочных линий пока нет</h3>
          <p>
            Они появятся после реализованных инициатив и подтверждённых сделок.
          </p>
        </section>
      ) : (
        groups.map((group) => (
          <section className="cc-block" key={group.type}>
            <WorldSectionAssetHeader
              eyebrow={continuityTypeHint(group.type)}
              title={continuityTypePlural(group.type)}
              kind={continuityAsset(group.type).kind}
              code={continuityAsset(group.type).code}
            />
            <div className="cc-continuity-grid">
              {group.items.map((item) => (
                <ContinuityCard item={item} key={item.id} />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
function ContinuityHistory({ continuity }: { continuity: CampaignContinuity }) {
  const itemById = new Map(
    [...continuity.active, ...continuity.archived].map((item) => [
      item.id,
      item,
    ]),
  );
  return (
    <div className="cc-tab-stack cc-continuity-history">
      <section className="cc-block">
        <BlockHeader
          eyebrow="Связь между циклами"
          title="Линии и последствия"
          icon="book"
        />
        <p className="cc-form-intro">
          Хроника хранит не только главы, но и путь каждого проекта, соглашения
          и устойчивого решения: когда оно появилось, получило развитие или
          завершилось.
        </p>
      </section>
      {continuity.archived.length > 0 && (
        <section className="cc-block">
          <h2>Завершённые линии</h2>
          <div className="cc-continuity-grid">
            {continuity.archived.map((item) => (
              <ContinuityCard item={item} key={item.id} />
            ))}
          </div>
        </section>
      )}
      <section className="cc-block">
        <h2>История изменений</h2>
        {continuity.history.length === 0 ? (
          <p className="cc-empty-inline">Изменений пока не зафиксировано.</p>
        ) : (
          <div className="cc-continuity-timeline">
            {continuity.history.map((event) => {
              const item = itemById.get(event.itemId);
              return (
                <article key={event.id}>
                  <span
                    className={`cc-continuity-dot ${event.toStatus.toLowerCase()}`}
                  />
                  <div>
                    <small>
                      {event.cycleId ? "Событие кампании" : "Основание линии"} ·{" "}
                      {formatContinuityDate(event.createdAt)}
                    </small>
                    <h3>{event.title}</h3>
                    {item && <b>{item.title}</b>}
                    <ParagraphText text={event.narrativeText} />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
function ContinuityCard({ item }: { item: CampaignContinuityItem }) {
  const showProgress = item.type === "PROJECT" || item.type === "STORY_THREAD";
  return (
    <article className={`cc-continuity-card ${item.status.toLowerCase()}`}>
      <header>
        <div className="cc-world-card-head cc-continuity-card-copy">
          <WorldAssetIcon
            kind={continuityAsset(item.type).kind}
            code={continuityAsset(item.type).code}
            label={item.title}
          />
          <div>
            <span>{continuityTypeLabel(item.type)}</span>
            {item.sourceType === "NPC_VISIT" && (
              <em className="cc-continuity-source npc">NPC</em>
            )}
            <h3>{item.title}</h3>
          </div>
        </div>
        <b>{continuityStatusLabel(item.status)}</b>
      </header>
      <ParagraphText text={item.summary} />
      <div className="cc-continuity-state">
        <small>Сейчас</small>
        <ParagraphText text={item.currentStateText} />
      </div>
      {showProgress && (
        <div className="cc-continuity-progress">
          <ProgressBar value={item.progressPercent} />
          <span>{item.progressPercent}%</span>
        </div>
      )}
      {item.status === "ACTIVE" && item.nextMilestoneText && (
        <div className="cc-continuity-next">
          <small>Следующий рубеж</small>
          <ParagraphText text={item.nextMilestoneText} />
        </div>
      )}
      <footer>
        {item.dueCycleNumber && item.status === "ACTIVE" ? (
          <span>Срок: цикл {item.dueCycleNumber}</span>
        ) : (
          <span>{continuityVisibilityLabel(item.visibility)}</span>
        )}
        <time>{formatContinuityDate(item.updatedAt)}</time>
      </footer>
    </article>
  );
}
const continuityTypeOrder: CampaignContinuityItem["type"][] = [
  "PROJECT",
  "AGREEMENT",
  "PROMISE",
  "POLICY",
  "STORY_THREAD",
];
function continuityTypeLabel(type: CampaignContinuityItem["type"]) {
  return {
    PROJECT: "Проект",
    AGREEMENT: "Соглашение",
    PROMISE: "Обещание",
    POLICY: "Постоянное решение",
    STORY_THREAD: "Открытая линия",
  }[type];
}
function continuityTypePlural(type: CampaignContinuityItem["type"]) {
  return {
    PROJECT: "Долгосрочные проекты",
    AGREEMENT: "Действующие соглашения",
    PROMISE: "Обещания Совета",
    POLICY: "Постоянные решения",
    STORY_THREAD: "Открытые линии",
  }[type];
}
function continuityTypeHint(type: CampaignContinuityItem["type"]) {
  return {
    PROJECT: "Работа продолжается",
    AGREEMENT: "Условия остаются в силе",
    PROMISE: "Совет взял обязательство",
    POLICY: "Новый порядок мира",
    STORY_THREAD: "Сюжет требует продолжения",
  }[type];
}
function continuityStatusLabel(status: CampaignContinuityItem["status"]) {
  return {
    ACTIVE: "Действует",
    COMPLETED: "Завершено",
    FAILED: "Не удалось",
    BROKEN: "Нарушено",
    EXPIRED: "Срок истёк",
    CANCELLED: "Отменено",
  }[status];
}
function continuityVisibilityLabel(
  visibility: CampaignContinuityItem["visibility"],
) {
  return visibility === "PUBLIC"
    ? "Публично"
    : visibility === "PRIVATE"
      ? "Лично"
      : "Для группы";
}
function formatContinuityDate(value: string) {
  return new Date(value).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export {
  InfrastructureWorldSection,
  InfrastructureProjectGroup,
  ThreatWorldSection,
  ThreatCard,
  ThreatHistory,
  threatLevelLabel,
  threatEventLabel,
  ContinuityWorldSection,
  ContinuityHistory,
  ContinuityCard,
  continuityTypeOrder,
  continuityTypeLabel,
  continuityTypePlural,
  continuityTypeHint,
  continuityStatusLabel,
  continuityVisibilityLabel,
  formatContinuityDate,
};
